import { useEffect, useState } from "react";
import { Autocomplete, Box, TextField, Card, CardContent, CardMedia, Typography, Button, ListSubheader } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { HiMapPin, HiEye, HiHeart } from "react-icons/hi2";
import { useDispatch, useSelector } from "react-redux";
import { getUsedBikesUnauthorized } from "../../State/UsedBike/Action";
import api from "../../services/api";
import AppPagination from "../Tools/AppPagination";
import UsedBikeDetailShort from "./UsedBikeDetailShort";
import { getBikeNames } from "../../State/NewBike/Action";
import { getStatesWithCities } from "../../State/State/Action";

export default function UsedBikeListShort({ modelId = "" }) {
  const [showBikeDetail, setShowBikeDetail] = useState(false);
  const [bikeId, setBikeId] = useState("");
  const [page, setPage] = useState(0);
  const [model, setModel] = useState(modelId);
  const [selectedCity, setSelectedCity] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const bikeNames = useSelector(
    (store) => store.newBikesResponse.bikeNames.bikeModelNames
  );

  const bikes = useSelector(
    (store) => store.usedBikesResponse.usedBikesPaged.usedBikes
  );
  const totalPages = useSelector(
    (store) => store.usedBikesResponse.usedBikesPaged.totalPages
  );

  const { statesWithCities = [], loading: statesLoading = false } = useSelector(
    (store) => store.stateResponse || {}
  );

  // Flatten cities with state information for Autocomplete
  // Sort by state name first, then by city name to ensure proper grouping
  const cityOptions = statesWithCities
    .flatMap((state) =>
      (state.cities || []).map((city) => ({
        ...city,
        stateName: state.stateName,
        stateId: state.stateId,
        label: city.cityName,
      }))
    )
    .sort((a, b) => {
      // First sort by state name
      if (a.stateName !== b.stateName) {
        return a.stateName.localeCompare(b.stateName);
      }
      // Then sort by city name within the same state
      return a.cityName.localeCompare(b.cityName);
    });

  useEffect(() => {
    dispatch(getBikeNames());
    dispatch(getStatesWithCities());
  }, [dispatch]);

  useEffect(() => {
    setPage(0); // Reset to first page when model or city changes
  }, [model, selectedCity]);

  useEffect(() => {
    const cityId = selectedCity?.cityId || "";
    dispatch(getUsedBikesUnauthorized(page, model, cityId));
  }, [page, model, selectedCity, dispatch]);

  const handleViewMore = (e, bike) => {
    navigate("/usedBikeDetailShort?bikeId=" + bike);
  };

  const handleBikeChange = (event, modelObj) => {
    setModel(modelObj ? modelObj.id : ""); // Ensure proper state update
  };

  const defaultProps = {
    options: bikeNames,
    getOptionLabel: (option) => option.name,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {!showBikeDetail ? (
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Modern Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center mb-4">
              <HiHeart className="w-8 h-8 text-red-500 mr-3" />
              <h1 className="text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                Used Bikes
              </h1>
            </div>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Find your perfect pre-owned bike with great value and trusted quality
            </p>
          </motion.div>

          {/* Modern Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="flex flex-col sm:flex-row justify-center gap-4 mb-8"
          >
            <Autocomplete
              onChange={handleBikeChange}
              onInputChange={(event, value, reason) => {
                if (reason === "clear") {
                  setModel("");
                }
              }}
              disablePortal
              {...defaultProps}
              sx={{ 
                width: { xs: "100%", sm: 300 },
                backgroundColor: "white", 
                borderRadius: "16px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "16px",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#10b981",
                  },
                }
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Search Used Bikes" 
                  placeholder="Find your perfect pre-owned ride..."
                />
              )}
            />
            <Autocomplete
              value={selectedCity}
              onChange={(event, newValue) => {
                setSelectedCity(newValue);
              }}
              loading={statesLoading}
              sx={{
                width: { xs: "100%", sm: 300 },
                backgroundColor: "white",
                borderRadius: "16px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "16px",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#10b981",
                  },
                }
              }}
              options={cityOptions}
              groupBy={(option) => option.stateName}
              getOptionLabel={(option) => option.label || option.cityName}
              renderGroup={(params) => (
                <li key={params.key}>
                  <ListSubheader
                    component="div"
                    sx={{
                      backgroundColor: "#f5f5f5",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      color: "#374151",
                      py: 1,
                      px: 2,
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    {params.group}
                  </ListSubheader>
                  <ul style={{ padding: 0, margin: 0 }}>
                    {params.children}
                  </ul>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by City"
                  placeholder="Select city..."
                />
              )}
            />
          </motion.div>

          {/* Modern Bike Grid */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
          >
            {bikes?.map((bike, index) => (
              <motion.div
                key={bike.bikeId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="group"
              >
                <Card 
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border-0"
                  sx={{ 
                    borderRadius: "16px",
                    "&:hover": {
                      boxShadow: "0 25px 50px rgba(0,0,0,0.15)"
                    }
                  }}
                >
                  <div className="relative overflow-hidden">
                    <CardMedia
                      component="img"
                      height="240"
                      image={bike.imageUrl}
                      alt={bike.brand + " " + bike.model}
                      className="cursor-pointer transition-transform duration-300 group-hover:scale-110"
                      onClick={(e) => handleViewMore(e, bike.bikeId)}
                      sx={{
                        width: "100%",
                        height: "240px",
                        objectFit: "contain",
                        backgroundColor: "#f5f5f5",
                      }}
                    />
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg">
                      <HiHeart className="w-5 h-5 text-red-500" />
                    </div>
                    <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Used
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <Typography 
                      variant="h6" 
                      className="font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors"
                      sx={{ fontSize: "1.1rem" }}
                    >
                      {bike.brand} {bike.model}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      className="text-gray-500 mb-3"
                      sx={{ fontSize: "0.9rem" }}
                    >
                      {bike.modelYear} Model
                    </Typography>
                    
                    <div className="flex items-center mb-3">
                      <HiMapPin className="w-4 h-4 text-gray-400 mr-1" />
                      <Typography 
                        variant="body2" 
                        className="text-gray-500"
                        sx={{ fontSize: "0.85rem" }}
                      >
                        {bike.sellerCity}
                      </Typography>
                    </div>
                    
                    <div className="flex items-center justify-between mb-4">
                      <Typography 
                        variant="h6" 
                        className="font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent"
                        sx={{ fontSize: "1.2rem" }}
                      >
                        ₹{bike.price.toLocaleString()}/-
                      </Typography>
                    </div>
                    
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={(e) => handleViewMore(e, bike.bikeId)}
                      className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl py-3 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                      startIcon={<HiEye className="w-5 h-5" />}
                      sx={{
                        borderRadius: "12px",
                        textTransform: "none",
                        fontSize: "1rem",
                        fontWeight: 600,
                        py: 1.5
                      }}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Modern Pagination */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="flex justify-center mt-12"
          >
            <Box className="bg-white rounded-2xl shadow-lg p-4">
              <AppPagination totalPages={totalPages} setPage={setPage} />
            </Box>
          </motion.div>
        </div>
      ) : (
        <UsedBikeDetailShort bikeId={bikeId} />
      )}
    </div>
  );
}
